import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogTrigger } from './dialog';
import { Button } from './button';
import { Input } from './input';
import { Checkbox } from './checkbox';
import { Star, Loader2, Trash2, X } from 'lucide-react';
import { useToast } from './use-toast';

const API_URL = process.env.REACT_APP_API_URL ? `${process.env.REACT_APP_API_URL}/api` : '/api';
const MAX_TEXT = 2000;
const MAX_NAME = 60;

const StarPicker = ({ value, onChange, disabled }) => {
    const [hover, setHover] = useState(0);
    const active = hover || value;
    return (
        <div className="flex gap-1" onMouseLeave={() => setHover(0)}>
            {[1, 2, 3, 4, 5].map((n) => (
                <button
                    key={n}
                    type="button"
                    disabled={disabled}
                    onMouseEnter={() => setHover(n)}
                    onClick={() => onChange(n)}
                    aria-label={`${n} star${n === 1 ? '' : 's'}`}
                    className={`p-1 rounded transition-colors ${disabled ? 'cursor-not-allowed' : 'cursor-pointer hover:bg-gray-100'}`}
                >
                    <Star
                        className={`h-7 w-7 ${n <= active ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`}
                    />
                </button>
            ))}
        </div>
    );
};

// Caller can provide its own trigger via children; otherwise the form renders
// a default "Leave a review" button. Pre-fills with the existing review if the
// user already wrote one — editing resets approval, mirroring the backend.
//
// Controlled mode: pass `open` + `onOpenChange` to drive the dialog from the
// outside. Useful when the trigger would unmount with its parent (e.g. inside
// a Popover) before the dialog mounts — children can be omitted entirely so
// no trigger button renders, only the dialog.
const ReviewForm = ({ children, onChange, open: controlledOpen, onOpenChange }) => {
    const { toast } = useToast();
    const isControlled = controlledOpen !== undefined;
    const [internalOpen, setInternalOpen] = useState(false);
    const open = isControlled ? controlledOpen : internalOpen;
    const setOpen = isControlled ? (onOpenChange || (() => {})) : setInternalOpen;
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [existing, setExisting] = useState(null);
    const [rating, setRating] = useState(0);
    const [text, setText] = useState('');
    const [displayName, setDisplayName] = useState('');
    const [anonymous, setAnonymous] = useState(false);

    const fetchMine = async () => {
        const token = localStorage.getItem('token');
        if (!token) return;
        setLoading(true);
        try {
            const r = await fetch(`${API_URL}/reviews/mine`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!r.ok) throw new Error('Failed to load review');
            const data = await r.json();
            const mine = data.review;
            setExisting(mine);
            setRating(mine?.rating || 0);
            setText(mine?.text || '');
            setDisplayName(mine?.displayName || '');
            setAnonymous(mine ? mine.displayName == null : false);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (open) fetchMine();
    }, [open]);

    const submit = async () => {
        if (rating < 1 || rating > 5) {
            toast({ title: 'Pick a rating', description: 'Please select 1–5 stars.', variant: 'destructive', duration: 3000 });
            return;
        }
        const token = localStorage.getItem('token');
        if (!token) return;
        setSubmitting(true);
        try {
            const r = await fetch(`${API_URL}/reviews`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                    rating,
                    text: text.trim() || null,
                    anonymous,
                    displayName: anonymous ? null : displayName.trim() || null,
                }),
            });
            const data = await r.json();
            if (!r.ok) throw new Error(data.error || 'Failed to submit review');
            toast({
                title: existing ? 'Review updated' : 'Review submitted',
                // Editing resets approval — be honest about it so the user
                // doesn't wonder why their review disappeared from the
                // landing page.
                description: 'Thanks! It will appear after admin approval.',
                duration: 4000,
            });
            setOpen(false);
            onChange?.();
        } catch (e) {
            toast({ title: 'Error', description: e.message, variant: 'destructive', duration: 4000 });
        } finally {
            setSubmitting(false);
        }
    };

    const remove = async () => {
        const token = localStorage.getItem('token');
        if (!token) return;
        if (!window.confirm('Delete your review?')) return;
        setSubmitting(true);
        try {
            const r = await fetch(`${API_URL}/reviews/mine`, {
                method: 'DELETE',
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!r.ok) throw new Error('Failed to delete review');
            toast({ title: 'Review deleted', duration: 3000 });
            setOpen(false);
            onChange?.();
        } catch (e) {
            toast({ title: 'Error', description: e.message, variant: 'destructive', duration: 4000 });
        } finally {
            setSubmitting(false);
        }
    };

    // Skip the trigger entirely when fully controlled with no children — the
    // caller is wiring open/onOpenChange themselves and there's no button to
    // render alongside the dialog.
    const showTrigger = !isControlled || children !== undefined;

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            {showTrigger && (
                <DialogTrigger asChild>
                    {children || (
                        <Button variant="outline" className="rounded-2xl">
                            <Star className="h-4 w-4 mr-2" />
                            Leave a review
                        </Button>
                    )}
                </DialogTrigger>
            )}
            <DialogContent className="bg-white rounded-2xl p-0 max-w-md">
                <div className="flex justify-between items-center px-5 pt-4 pb-3 border-b border-gray-100">
                    <h3 className="text-lg font-semibold">
                        {existing ? 'Edit your review' : 'Leave a review'}
                    </h3>
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setOpen(false)}
                        className="text-gray-500 hover:text-gray-900"
                    >
                        <X className="h-5 w-5" />
                    </Button>
                </div>

                {loading ? (
                    <div className="px-5 py-8 flex justify-center">
                        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                    </div>
                ) : (
                    <div className="px-5 py-4 space-y-4">
                        <div>
                            <label className="text-sm font-medium text-gray-700">Rating</label>
                            <StarPicker value={rating} onChange={setRating} disabled={submitting} />
                        </div>

                        <div>
                            <label className="text-sm font-medium text-gray-700">
                                Your review <span className="text-gray-400">(optional)</span>
                            </label>
                            <textarea
                                value={text}
                                onChange={(e) => setText(e.target.value.slice(0, MAX_TEXT))}
                                rows={4}
                                placeholder="Tell others what you liked…"
                                className="mt-1 w-full rounded-md border border-gray-300 p-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-400"
                            />
                            <div className="text-right text-xs text-gray-400">
                                {text.length}/{MAX_TEXT}
                            </div>
                        </div>

                        <div className="space-y-2">
                            <div className="flex items-center gap-2">
                                <Checkbox
                                    id="review-anon"
                                    checked={anonymous}
                                    onCheckedChange={(v) => setAnonymous(v === true)}
                                    disabled={submitting}
                                />
                                <label htmlFor="review-anon" className="text-sm text-gray-700 cursor-pointer">
                                    Post anonymously
                                </label>
                            </div>

                            {!anonymous && (
                                <Input
                                    value={displayName}
                                    onChange={(e) => setDisplayName(e.target.value.slice(0, MAX_NAME))}
                                    placeholder="Display name (shown publicly)"
                                    className="rounded-md w-auto"
                                    disabled={submitting}
                                />
                            )}
                        </div>

                        {existing && !existing.approved && (
                            <p className="text-xs text-gray-500">
                                Your review is awaiting admin approval before it appears publicly.
                            </p>
                        )}
                    </div>
                )}

                <div className="px-5 py-3 border-t border-gray-100 flex justify-between items-center bg-gray-50 rounded-b-2xl">
                    {existing ? (
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={remove}
                            disabled={submitting}
                            className="text-red-600 hover:bg-red-50 hover:text-red-700 rounded-xl"
                        >
                            <Trash2 className="h-4 w-4 mr-1" />
                            Delete
                        </Button>
                    ) : <span />}

                    <div className="flex gap-2">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setOpen(false)}
                            disabled={submitting}
                            className="rounded-xl"
                        >
                            Cancel
                        </Button>
                        <Button
                            size="sm"
                            onClick={submit}
                            disabled={submitting || rating < 1}
                            className="bg-gray-900 text-white rounded-xl"
                        >
                            {submitting && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
                            {existing ? 'Save changes' : 'Submit'}
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default ReviewForm;
